import React, { useState, useEffect } from 'react';
import { getGeneralInsuranceList, approveGeneralInsurance, rejectGeneralInsurance, rollbackGeneralInsurance, editApprovedGeneralInsurance, editPendingGeneralInsurance, getImageUrl } from '../model/api';
import { toast } from 'react-toastify';
import GeneralInsuranceForm from './GeneralInsuranceForm';
import './GeneralInsuranceList.css';

const GeneralInsuranceList = ({ userRole, user }) => {
    const [applications, setApplications] = useState([]);
    const [ownPolicies, setOwnPolicies] = useState([]);
    const [teamPolicies, setTeamPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedApp, setSelectedApp] = useState(null);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPendingEditModal, setShowPendingEditModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [policyPdf, setPolicyPdf] = useState(null);
    const [policyAmount, setPolicyAmount] = useState('');
    const [rejectionComment, setRejectionComment] = useState('');
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [showNewApplicationForm, setShowNewApplicationForm] = useState(false);

    // Pending edit form fields
    const [editCustomerName, setEditCustomerName] = useState('');
    const [editCustomerPhone, setEditCustomerPhone] = useState('');
    const [editCustomerEmail, setEditCustomerEmail] = useState('');
    const [editVehicleNumber, setEditVehicleNumber] = useState('');
    const [editComments, setEditComments] = useState('');


    // Enhanced approval fields
    const [vehicleNumber, setVehicleNumber] = useState('');
    const [policyNumber, setPolicyNumber] = useState('');
    const [insuranceCompany, setInsuranceCompany] = useState('');
    const [insuranceValue, setInsuranceValue] = useState('');
    const [gstPercentage, setGstPercentage] = useState('18');
    const [totalAmount, setTotalAmount] = useState('0');

    // Commission fields
    const [commissionPercentage, setCommissionPercentage] = useState('');
    const [hierarchyPercentage, setHierarchyPercentage] = useState('10');

    useEffect(() => {
        fetchApplications();
    }, [filter, selectedMonth, searchTerm]); // Added searchTerm to dependency array

    const fetchApplications = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter !== 'all') {
                params.status = filter;
            }
            if (selectedMonth) {
                params.month = selectedMonth;
            }
            if (searchTerm) { // Add searchTerm to API parameters
                params.search = searchTerm;
            }
            const result = await getGeneralInsuranceList(params);
            if (result.ok) {
                const allApplications = result.data.applications || [];
                setApplications(allApplications);

                // Separate leader's own policies from team policies
                if (userRole === 'leader' && user) {
                    const ownPolicies = allApplications.filter(app =>
                        app.leader_id?._id === user.id && app.submitted_by_role === 'leader'
                    );
                    const teamPolicies = allApplications.filter(app =>
                        app.leader_id?._id === user.id && app.submitted_by_role !== 'leader'
                    );
                    setOwnPolicies(ownPolicies);
                    setTeamPolicies(teamPolicies);
                }
            }
        } catch (error) {
            console.error('Error fetching applications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!policyPdf || !policyAmount) {
            toast.warning('Please provide policy PDF and amount');
            return;
        }

        if (!policyNumber || !insuranceCompany) {
            alert('Please provide policy number and insurance company');
            return;
        }

        if (!commissionPercentage) {
            toast.warning('Please provide commission percentage');
            return;
        }

        setApproving(true);
        try {
            const formData = new FormData();
            formData.append('policy_pdf', policyPdf);
            formData.append('policy_amount', policyAmount);
            formData.append('vehicle_number', vehicleNumber);
            formData.append('policy_number', policyNumber);
            formData.append('insurance_company', insuranceCompany);
            formData.append('insurance_value', insuranceValue);
            formData.append('gst_amount', (parseFloat(insuranceValue || 0) * parseFloat(gstPercentage || 0) / 100).toFixed(2));
            formData.append('invoice_value', totalAmount);
            formData.append('payout_percentage', '20'); // Default, can be made configurable
            formData.append('commission_percentage', commissionPercentage);
            formData.append('hierarchy_percentage', hierarchyPercentage);

            const result = await approveGeneralInsurance(selectedApp._id, formData);
            if (result.ok) {
                toast.success('Application approved successfully!');
                setShowApproveModal(false);
                setSelectedApp(null);
                resetApprovalForm();
                fetchApplications();
            }
        } catch (error) {
            toast.error('Error approving application: ' + error.message);
        } finally {
            setApproving(false);
        }
    };

    const resetApprovalForm = () => {
        setPolicyPdf(null);
        setPolicyAmount('');
        setVehicleNumber('');
        setPolicyNumber('');
        setInsuranceCompany('');
        setInsuranceValue('');
        setGstPercentage('18');
        setTotalAmount('0');
        setCommissionPercentage('');
        setHierarchyPercentage('10');
    };

    const handleReject = async () => {
        if (!rejectionComment.trim()) {
            toast.warning(userRole === 'super_admin' ? 'Please provide a rejection comment' : 'Please provide a reason for cancellation');
            return;
        }

        setRejecting(true);
        try {
            let result;
            if (userRole === 'super_admin') {
                // Admin rejects the application
                result = await rejectGeneralInsurance(selectedApp._id, rejectionComment);
            } else {
                // Leader/Advisor rolls back their own application
                result = await rollbackGeneralInsurance(selectedApp._id, rejectionComment);
            }

            if (result.ok) {
                toast.success(userRole === 'super_admin' ? 'Application rejected successfully!' : 'Application cancelled successfully!');
                setShowRejectModal(false);
                setSelectedApp(null);
                setRejectionComment('');
                fetchApplications();
            }
        } catch (error) {
            toast.error('Error: ' + error.message);
        } finally {
            setRejecting(false);
        }
    };

    const handleEditClick = (app) => {
        setSelectedApp(app);
        // Pre-fill form with current values
        setPolicyAmount(app.policy_amount || '');
        setVehicleNumber(app.vehicle_number || '');
        setPolicyNumber(app.policy_number || '');
        setInsuranceCompany(app.insurance_company || '');
        setInsuranceValue(app.insurance_value || '');
        setGstPercentage(app.gst_percentage || '18');
        setCommissionPercentage(app.commission_percentage || '');
        setHierarchyPercentage(app.hierarchy_percentage || '10');
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        if (!policyAmount) {
            toast.warning('Please provide policy amount');
            return;
        }

        setEditing(true);
        try {
            const editData = {
                customer_name: selectedApp.customer_name,
                customer_phone: selectedApp.customer_phone,
                vehicle_number: vehicleNumber,
                policy_number: policyNumber,
                insurance_company: insuranceCompany,
                insurance_value: parseFloat(insuranceValue) || 0,
                gst_amount: parseFloat(insuranceValue) * (parseFloat(gstPercentage) / 100) || 0,
                policy_amount: parseFloat(policyAmount),
                commission_percentage: parseFloat(commissionPercentage),
                hierarchy_percentage: parseFloat(hierarchyPercentage)
            };

            const result = await editApprovedGeneralInsurance(selectedApp._id, editData);

            if (result.ok) {
                toast.success(result.message || 'Application updated successfully!');
                setShowEditModal(false);
                setSelectedApp(null);
                resetApprovalForm();
                fetchApplications();
            }
        } catch (error) {
            toast.error('Error updating application: ' + error.message);
        } finally {
            setEditing(false);
        }
    };

    const handlePendingEditClick = (app) => {
        setSelectedApp(app);
        // Pre-fill form with current values
        setEditCustomerName(app.customer_name || '');
        setEditCustomerPhone(app.customer_phone || '');
        setEditCustomerEmail(app.customer_email || '');
        setEditVehicleNumber(app.vehicle_number || '');
        setEditComments(app.comments || '');
        setShowPendingEditModal(true);
    };

    const handlePendingEditSubmit = async () => {
        if (!editCustomerName || !editCustomerPhone) {
            toast.warning('Customer name and phone are required');
            return;
        }

        setEditing(true);
        try {
            const editData = {
                customer_name: editCustomerName,
                customer_phone: editCustomerPhone,
                customer_email: editCustomerEmail,
                vehicle_number: editVehicleNumber,
                comments: editComments
            };

            const result = await editPendingGeneralInsurance(selectedApp._id, editData);

            if (result.ok) {
                toast.success(result.message || 'Application updated successfully!');
                setShowPendingEditModal(false);
                setSelectedApp(null);
                // Reset form
                setEditCustomerName('');
                setEditCustomerPhone('');
                setEditCustomerEmail('');
                setEditVehicleNumber('');
                setEditComments('');
                fetchApplications();
            }
        } catch (error) {
            toast.error('Error updating application: ' + error.message);
        } finally {
            setEditing(false);
        }
    };

    // Helper function to check if application is from current month
    const isCurrentMonth = (createdAt) => {
        const now = new Date();
        const appDate = new Date(createdAt);
        return now.getFullYear() === appDate.getFullYear() && now.getMonth() === appDate.getMonth();
    };


    const getStatusBadge = (status) => {
        const badges = {
            pending: <span className="badge badge-warning">Pending</span>,
            approved: <span className="badge badge-success">Approved</span>,
            rejected: <span className="badge badge-danger">Rejected</span>
        };
        return badges[status] || status;
    };

    const renderApplicationCard = (app, viewType = 'default') => {
        const isSuperAdmin = userRole === 'super_admin';
        // Check if user is the direct owner (Advisor who submitted it, or Leader who submitted it personally)
        // Helper to safely get string ID
        const getSafeId = (obj) => {
            if (!obj) return null;
            if (typeof obj === 'string') return obj;
            return obj._id || obj.id;
        };

        const currentUserId = getSafeId(user);
        const appAdvisorId = getSafeId(app.advisor_id);
        const appLeaderId = getSafeId(app.leader_id);

        // Debug log to trace permission issues (View in Browser Console)
        console.log(`[GI_DEBUG] App: ${app.application_id}`, {
            user: currentUserId,
            advisor: appAdvisorId,
            leader: appLeaderId,
            role: app.submitted_by_role,
            isOwnerMatch: (appAdvisorId?.toString() === currentUserId?.toString())
        });

        const isOwner = user && (
            (appAdvisorId?.toString() === currentUserId?.toString()) ||
            (appLeaderId?.toString() === currentUserId?.toString() && app.submitted_by_role === 'leader')
        );

        // 1. SUPER ADMIN VIEW - Full Access
        if (isSuperAdmin) {
            return (
                <>
                    <p><strong>Type:</strong> {app.insurance_type}</p>
                    <p><strong>Customer:</strong> {app.customer_name}</p>
                    <p><strong>Phone:</strong> {app.customer_phone}</p>
                    {app.vehicle_number && <p><strong>Vehicle:</strong> {app.vehicle_number}</p>}
                    <p><strong>Submitted by:</strong> {app.submitted_by_role === 'advisor' ? app.advisor_id?.name : app.leader_id?.name} ({app.submitted_by_role})</p>
                    <p><strong>Submitted:</strong> {new Date(app.createdAt).toLocaleDateString()}</p>

                    {app.status === 'approved' && (
                        <>
                            <p><strong>Amount:</strong> ₹{app.policy_amount}</p>
                            <p><strong>Commission:</strong> ₹{app.commission_amount?.toFixed(2)}</p>

                            {app.approved_policy_pdf && (
                                <a href={getImageUrl(app.approved_policy_pdf)} target="_blank" rel="noopener noreferrer" className="view-policy">
                                    View Policy PDF
                                </a>
                            )}
                        </>
                    )}

                    {app.status === 'rejected' && app.rejection_comment && (
                        <p className="rejection-comment"><strong>Reason:</strong> {app.rejection_comment}</p>
                    )}

                    {app.documents && (
                        <div className="documents-section" style={{ marginTop: '10px' }}>
                            <p><strong>Documents:</strong></p>
                            <div className="document-links" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                {app.documents.rc_document && (
                                    <a href={getImageUrl(app.documents.rc_document)} target="_blank" rel="noopener noreferrer" className="doc-link">
                                        RC Document
                                    </a>
                                )}
                                {app.documents.existing_insurance && (
                                    <a href={getImageUrl(app.documents.existing_insurance)} target="_blank" rel="noopener noreferrer" className="doc-link">
                                        Existing Insurance
                                    </a>
                                )}
                                {app.documents.aadhar && (
                                    <a href={getImageUrl(app.documents.aadhar)} target="_blank" rel="noopener noreferrer" className="doc-link">
                                        Aadhar Card
                                    </a>
                                )}
                                {app.documents.pan_card && (
                                    <a href={getImageUrl(app.documents.pan_card)} target="_blank" rel="noopener noreferrer" className="doc-link">
                                        PAN Card
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {app.additional_photos && app.additional_photos.length > 0 && (
                        <div className="additional-photos">
                            <p><strong>Additional Photos:</strong></p>
                            <div className="photo-links">
                                {app.additional_photos.map((photoUrl, index) => (
                                    <a
                                        key={index}
                                        href={getImageUrl(photoUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="photo-link"
                                    >
                                        Photo {index + 1}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            );
        }

        // 2. OWNER VIEW (Submitter) - Restricted View
        if (isOwner) {
            return (
                <>
                    <p><strong>Type:</strong> {app.insurance_type}</p>
                    <p><strong>Customer:</strong> {app.customer_name}</p>
                    <p><strong>Phone:</strong> {app.customer_phone}</p>
                    {app.vehicle_number && <p><strong>Vehicle:</strong> {app.vehicle_number}</p>}
                    <p><strong>Submitted by:</strong> {app.submitted_by_role === 'advisor' ? app.advisor_id?.name : app.leader_id?.name} ({app.submitted_by_role})</p>

                    {app.status === 'approved' && (
                        <>
                            {/* Show Policy Amount if needed, or just Commission? Request said "Amount: ..." in Rama's leader section.
                                For submitter, usually they see amount. Keeping it. */}
                            {/* <p><strong>Amount:</strong> ₹{app.policy_amount}</p> */}

                            {app.user_payout && (
                                <div style={{
                                    marginTop: '10px',
                                    padding: '10px',
                                    backgroundColor: '#e8f5e9',
                                    borderRadius: '4px',
                                    border: '1px solid #4caf50'
                                }}>
                                    <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#2e7d32' }}>
                                        💰 Your Commission
                                    </p>
                                    <p style={{ margin: '0 0 5px 0' }}>
                                        <strong>Amount:</strong> ₹{app.user_payout.payout_amount?.toFixed(2)}
                                    </p>
                                    <p style={{ margin: '0' }}>
                                        <strong>Status:</strong>
                                        <span style={{
                                            marginLeft: '5px',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            backgroundColor: app.user_payout.status === 'paid' ? '#d1fae5' : '#fef3c7',
                                            color: app.user_payout.status === 'paid' ? '#065f46' : '#92400e'
                                        }}>
                                            {app.user_payout.status === 'paid' ? '✓ Paid' : 'Unpaid'}
                                        </span>
                                    </p>
                                    {app.user_payout.status === 'paid' && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#555', borderTop: '1px dashed #a5d6a7', paddingTop: '5px' }}>
                                            {app.user_payout.transaction_id && (
                                                <p style={{ margin: '2px 0' }}><strong>Txn ID:</strong> {app.user_payout.transaction_id}</p>
                                            )}
                                            {app.user_payout.paid_at && (
                                                <p style={{ margin: '2px 0' }}><strong>Paid:</strong> {new Date(app.user_payout.paid_at).toLocaleString('en-IN', {
                                                    day: '2-digit', month: 'short', year: 'numeric',
                                                    hour: '2-digit', minute: '2-digit'
                                                })}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {app.approved_policy_pdf && (
                                <a href={getImageUrl(app.approved_policy_pdf)} target="_blank" rel="noopener noreferrer" className="view-policy">
                                    View Policy PDF
                                </a>
                            )}
                        </>
                    )}

                    {/* Documents - Only RC allowed for Owner */}
                    <div className="documents-section" style={{ marginTop: '10px' }}>
                        <p><strong>Documents:</strong></p>
                        <div className="document-links" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {app.documents?.rc_document ? (
                                <a href={getImageUrl(app.documents.rc_document)} target="_blank" rel="noopener noreferrer" className="doc-link">
                                    RC Document
                                </a>
                            ) : (
                                <span style={{ color: '#999', fontSize: '13px' }}>No RC Document</span>
                            )}
                        </div>
                    </div>
                </>
            );
        }

        // 3. TEAM/UPLINE VIEW - Minimal View (Financial Only)
        // For leaders viewing downline policies
        return (
            <>
                {/* Intentional: No Customer Name, No Phone, No Vehicle */}
                <p><strong>Submitted by:</strong> {app.submitted_by_role === 'advisor' ? app.advisor_id?.name : app.leader_id?.name} ({app.submitted_by_role})</p>
                <p><strong>Submitted:</strong> {new Date(app.createdAt).toLocaleDateString()}</p>

                {app.status === 'approved' && (
                    <>
                        <p><strong>Amount:</strong> ₹{app.policy_amount}</p>

                        {app.user_payout && (
                            <div style={{
                                marginTop: '10px',
                                padding: '10px',
                                backgroundColor: '#e8f5e9',
                                borderRadius: '4px',
                                border: '1px solid #4caf50'
                            }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#2e7d32' }}>
                                    💰 Your Commission
                                </p>
                                <p style={{ margin: '0 0 5px 0' }}>
                                    <strong>Amount:</strong> ₹{app.user_payout.payout_amount?.toFixed(2)}
                                </p>
                                <p style={{ margin: '0' }}>
                                    <strong>Status:</strong>
                                    <span style={{
                                        marginLeft: '5px',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        backgroundColor: app.user_payout.status === 'paid' ? '#d1fae5' : '#fef3c7',
                                        color: app.user_payout.status === 'paid' ? '#065f46' : '#92400e'
                                    }}>
                                        {app.user_payout.status === 'paid' ? '✓ Paid' : 'Unpaid'}
                                    </span>
                                </p>
                                {app.user_payout.status === 'paid' && (
                                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#555', borderTop: '1px dashed #a5d6a7', paddingTop: '5px' }}>
                                        {app.user_payout.transaction_id && (
                                            <p style={{ margin: '2px 0' }}><strong>Txn ID:</strong> {app.user_payout.transaction_id}</p>
                                        )}
                                        {app.user_payout.paid_at && (
                                            <p style={{ margin: '2px 0' }}><strong>Paid:</strong> {new Date(app.user_payout.paid_at).toLocaleString('en-IN', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit'
                                            })}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </>
        );
    };

    if (loading) {
        return <div className="loading">Loading applications...</div>;
    }

    return (
        <div className="general-insurance-list">
            <div className="list-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h2>My Policies & Commission</h2>
                    {(userRole === 'advisor' || userRole === 'leader' || userRole === 'health_plus_partner') && (
                        <button
                            onClick={() => setShowNewApplicationForm(true)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#4f46e5',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <span style={{ fontSize: '18px' }}>+</span>
                            New Application
                        </button>
                    )}
                </div>

                {/* Search Bar */}
                <div className="search-bar" style={{ marginBottom: '15px' }}>
                    <input
                        type="text"
                        placeholder="Search by customer name, application ID, phone, vehicle number, or insurance type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                        style={{
                            width: '100%',
                            padding: '10px 15px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'border-color 0.3s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    />
                </div>

                <div className="filter-buttons">
                    <button
                        className={filter === 'all' ? 'active' : ''}
                        onClick={() => setFilter('all')}
                    >
                        All
                    </button>
                    <button
                        className={filter === 'pending' ? 'active' : ''}
                        onClick={() => setFilter('pending')}
                    >
                        Pending
                    </button>
                    <button
                        className={filter === 'approved' ? 'active' : ''}
                        onClick={() => setFilter('approved')}
                    >
                        Approved
                    </button>
                    <button
                        className={filter === 'rejected' ? 'active' : ''}
                        onClick={() => setFilter('rejected')}
                    >
                        Rejected
                    </button>
                </div>
                <div className="month-filter" style={{ marginTop: '15px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <label style={{ fontWeight: '500' }}>Filter by Month:</label>

                    {/* Month Select */}
                    <select
                        value={selectedMonth ? selectedMonth.split('-')[1] : ''}
                        onChange={(e) => {
                            const y = selectedMonth ? selectedMonth.split('-')[0] : new Date().getFullYear();
                            const m = e.target.value;
                            if (m === '') setSelectedMonth(''); // Clear if "All Time" selected
                            else setSelectedMonth(`${y}-${m}`);
                        }}
                        style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                        <option value="">All Time</option>
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                    </select>

                    {/* Year Select */}
                    <select
                        value={selectedMonth ? selectedMonth.split('-')[0] : ''}
                        onChange={(e) => {
                            const m = selectedMonth ? selectedMonth.split('-')[1] : new Date().getMonth() + 1;
                            const monthStr = String(m).padStart(2, '0');
                            const y = e.target.value;
                            if (y === '') setSelectedMonth('');
                            else setSelectedMonth(`${y}-${monthStr}`);
                        }}
                        style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                        <option value="">All Years</option>
                        {Array.from({ length: 11 }, (_, i) => 2025 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                    {selectedMonth && (
                        <button
                            onClick={() => setSelectedMonth('')}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            Clear Month Filter
                        </button>
                    )}
                </div>
            </div>

            {applications.length === 0 ? (
                <div className="no-data">No applications found</div>
            ) : (
                <div>
                    {/* Leader's Own Policies Section */}
                    {userRole === 'leader' && ownPolicies.length > 0 && (
                        <div className="policies-section" style={{ marginBottom: '30px' }}>
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                color: '#4f46e5',
                                marginBottom: '15px',
                                borderBottom: '2px solid #4f46e5',
                                paddingBottom: '8px'
                            }}>
                                My Own Policies
                            </h3>
                            <div className="applications-grid">
                                {ownPolicies.map(app => (
                                    <div key={app._id} className="application-card">
                                        <div className="card-header">
                                            <span className="app-id">{app.application_id}</span>
                                            {getStatusBadge(app.status)}
                                        </div>
                                        <div className="card-body">
                                            {renderApplicationCard(app)}
                                        </div>
                                        <div className="card-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                            {/* Leader can edit/cancel their own pending applications */}
                                            {app.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => handlePendingEditClick(app)}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#3b82f6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        ✎ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedApp(app);
                                                            setShowRejectModal(true);
                                                        }}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#f59e0b',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        ⟲ Cancel
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Team Policies Section */}
                    {userRole === 'leader' && teamPolicies.length > 0 && (
                        <div className="policies-section">
                            <h3 style={{
                                fontSize: '18px',
                                fontWeight: 'bold',
                                color: '#059669',
                                marginBottom: '15px',
                                borderBottom: '2px solid #059669',
                                paddingBottom: '8px'
                            }}>
                                Team Policies
                            </h3>
                            <div className="applications-grid">
                                {teamPolicies.map(app => (
                                    <div key={app._id} className="application-card">
                                        <div className="card-header">
                                            <span className="app-id">{app.application_id}</span>
                                            {getStatusBadge(app.status)}
                                        </div>
                                        <div className="card-body">
                                            {renderApplicationCard(app, 'team')}
                                        </div>
                                        {/* No action buttons for team policies - view only */}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Default view for non-leaders or when no separation needed */}
                    {userRole !== 'leader' && (
                        <div className="applications-grid">
                            {applications.map(app => (
                                <div key={app._id} className="application-card">
                                    <div className="card-header">
                                        <span className="app-id">{app.application_id}</span>
                                        {getStatusBadge(app.status)}
                                    </div>
                                    <div className="card-body">
                                        {renderApplicationCard(app)}
                                    </div>
                                    <div className="card-actions" style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        {/* Super Admin Actions */}
                                        {userRole === 'super_admin' && app.status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setSelectedApp(app);
                                                        setShowApproveModal(true);
                                                    }}
                                                    style={{
                                                        padding: '8px 16px',
                                                        backgroundColor: '#10b981',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    ✓ Approve
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setSelectedApp(app);
                                                        setShowRejectModal(true);
                                                    }}
                                                    style={{
                                                        padding: '8px 16px',
                                                        backgroundColor: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    ✗ Reject
                                                </button>
                                            </>
                                        )}

                                        {/* Edit button for approved applications (super_admin only) */}
                                        {userRole === 'super_admin' && app.status === 'approved' && (
                                            <button
                                                onClick={() => handleEditClick(app)}
                                                style={{
                                                    padding: '8px 16px',
                                                    backgroundColor: '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: '500'
                                                }}
                                            >
                                                ✎ Edit
                                            </button>
                                        )}

                                        {/* Cancel/Edit for own pending applications (leader/advisor) */}
                                        {(userRole === 'leader' || userRole === 'advisor') && app.status === 'pending' && (
                                            (app.advisor_id?._id === user?.id || app.leader_id?._id === user?.id) && (
                                                <>
                                                    <button
                                                        onClick={() => handlePendingEditClick(app)}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#3b82f6',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        ✎ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedApp(app);
                                                            setShowRejectModal(true);
                                                        }}
                                                        style={{
                                                            padding: '8px 16px',
                                                            backgroundColor: '#f59e0b',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            fontSize: '14px',
                                                            fontWeight: '500'
                                                        }}
                                                    >
                                                        ⟲ Cancel
                                                    </button>
                                                </>
                                            )
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Approve Modal */}
            {showApproveModal && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '600px' }}>
                        <h3>Approve Application - {selectedApp?.application_id}</h3>

                        <div className="form-group">
                            <label>Vehicle Number {['car', 'bike'].includes(selectedApp?.insurance_type) && '*'}</label>
                            <input
                                type="text"
                                value={vehicleNumber}
                                onChange={(e) => setVehicleNumber(e.target.value)}
                                placeholder="e.g., TN01AB1234"
                            />
                        </div>

                        <div className="form-group">
                            <label>Policy Number *</label>
                            <input
                                type="text"
                                value={policyNumber}
                                onChange={(e) => setPolicyNumber(e.target.value)}
                                placeholder="Enter policy number"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Insurance Company *</label>
                            <input
                                type="text"
                                value={insuranceCompany}
                                onChange={(e) => setInsuranceCompany(e.target.value)}
                                placeholder="e.g., ICICI Lombard, HDFC ERGO"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Insurance Value (₹) *</label>
                            <input
                                type="number"
                                value={insuranceValue}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setInsuranceValue(value);
                                    // Auto-calculate total
                                    const gst = (parseFloat(value || 0) * parseFloat(gstPercentage || 0) / 100);
                                    const total = parseFloat(value || 0) + gst;
                                    setTotalAmount(total.toFixed(2));
                                    setPolicyAmount(total.toFixed(2));
                                }}
                                placeholder="Enter insurance value"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Commission Percentage (%) *</label>
                            <input
                                type="number"
                                value={commissionPercentage}
                                onChange={(e) => setCommissionPercentage(e.target.value)}
                                placeholder="e.g., 10, 15, 20"
                                step="0.01"
                                min="0"
                                max="100"
                                required
                            />
                            <small style={{ color: '#666' }}>Commission rate for this policy</small>
                        </div>

                        <div className="form-group">
                            <label>Hierarchy Share Percentage (%)</label>
                            <input
                                type="number"
                                value={hierarchyPercentage}
                                onChange={(e) => setHierarchyPercentage(e.target.value)}
                                placeholder="Default: 10"
                                step="0.01"
                                min="0"
                                max="100"
                            />
                            <small style={{ color: '#666' }}>Percentage each leader gets from their subordinate's commission (Default: 10%)</small>
                        </div>

                        <div className="form-group">
                            <label>GST Percentage (%)</label>
                            <input
                                type="number"
                                value={gstPercentage}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setGstPercentage(value);
                                    // Auto-calculate total
                                    const gst = (parseFloat(insuranceValue || 0) * parseFloat(value || 0) / 100);
                                    const total = parseFloat(insuranceValue || 0) + gst;
                                    setTotalAmount(total.toFixed(2));
                                    setPolicyAmount(total.toFixed(2));
                                }}
                                placeholder="Default: 18"
                                step="0.01"
                            />
                            <small style={{ color: '#666' }}>Default: 18%</small>
                        </div>

                        <div className="form-group">
                            <label>GST Amount (₹)</label>
                            <input
                                type="text"
                                value={(parseFloat(insuranceValue || 0) * parseFloat(gstPercentage || 0) / 100).toFixed(2)}
                                disabled
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        </div>

                        <div className="form-group">
                            <label>Total Amount (₹) *</label>
                            <input
                                type="text"
                                value={totalAmount}
                                disabled
                                style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '1.1em' }}
                            />
                            <small style={{ color: '#666' }}>Insurance Value + GST</small>
                        </div>

                        <div className="form-group">
                            <label>Policy PDF *</label>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => setPolicyPdf(e.target.files[0])}
                            />
                            {policyPdf && <small style={{ color: 'green' }}>✓ {policyPdf.name}</small>}
                        </div>

                        <div className="modal-actions">
                            <button
                                onClick={() => {
                                    setShowApproveModal(false);
                                    resetApprovalForm();
                                }}
                                disabled={approving}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApprove}
                                className="btn-primary"
                                disabled={approving}
                            >
                                {approving ? 'Approving...' : 'Approve Policy'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedApp && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '600px' }}>
                        <h3>Edit Application - {selectedApp.application_id}</h3>

                        <div className="form-group">
                            <label>Vehicle Number</label>
                            <input
                                type="text"
                                value={vehicleNumber}
                                onChange={(e) => setVehicleNumber(e.target.value)}
                                placeholder="e.g., TN01AB1234"
                            />
                        </div>

                        <div className="form-group">
                            <label>Policy Number *</label>
                            <input
                                type="text"
                                value={policyNumber}
                                onChange={(e) => setPolicyNumber(e.target.value)}
                                placeholder="Enter policy number"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Insurance Company *</label>
                            <input
                                type="text"
                                value={insuranceCompany}
                                onChange={(e) => setInsuranceCompany(e.target.value)}
                                placeholder="e.g., ICICI Lombard, HDFC ERGO"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Insurance Value (₹) *</label>
                            <input
                                type="number"
                                value={insuranceValue}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setInsuranceValue(value);
                                    // Auto-calculate total
                                    const gst = (parseFloat(value || 0) * parseFloat(gstPercentage || 0) / 100);
                                    const total = parseFloat(value || 0) + gst;
                                    setTotalAmount(total.toFixed(2));
                                    setPolicyAmount(total.toFixed(2));
                                }}
                                placeholder="Enter insurance value"
                                required
                                step="0.01"
                            />
                        </div>

                        <div className="form-group">
                            <label>GST Percentage (%)</label>
                            <input
                                type="number"
                                value={gstPercentage}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setGstPercentage(value);
                                    // Auto-calculate total
                                    const gst = (parseFloat(insuranceValue || 0) * parseFloat(value || 0) / 100);
                                    const total = parseFloat(insuranceValue || 0) + gst;
                                    setTotalAmount(total.toFixed(2));
                                    setPolicyAmount(total.toFixed(2));
                                }}
                                placeholder="Default: 18"
                                step="0.01"
                            />
                            <small style={{ color: '#666' }}>Default: 18%</small>
                        </div>

                        <div className="form-group">
                            <label>Total Amount (₹) *</label>
                            <input
                                type="text"
                                value={totalAmount}
                                disabled
                                style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: '1.1em' }}
                            />
                            <small style={{ color: '#666' }}>Insurance Value + GST</small>
                        </div>

                        <div className="form-group">
                            <label>Commission Percentage (%) *</label>
                            <input
                                type="number"
                                value={commissionPercentage}
                                onChange={(e) => setCommissionPercentage(e.target.value)}
                                placeholder="e.g., 10"
                                required
                                step="0.01"
                                min="0"
                                max="100"
                            />
                            <small style={{ color: '#ff9800' }}>⚠️ Changing this will recalculate all payouts</small>
                        </div>

                        <div className="form-group">
                            <label>Hierarchy Percentage (%) *</label>
                            <input
                                type="number"
                                value={hierarchyPercentage}
                                onChange={(e) => setHierarchyPercentage(e.target.value)}
                                placeholder="Default: 10"
                                required
                                step="0.01"
                                min="0"
                                max="100"
                            />
                            <small style={{ color: '#666' }}>Default: 10% - Each leader gets this % of their subordinate's commission</small>
                        </div>

                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowEditModal(false);
                                    setSelectedApp(null);
                                    resetApprovalForm();
                                }}
                                disabled={editing}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-approve"
                                onClick={handleEditSubmit}
                                disabled={editing}
                            >
                                {editing ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pending Edit Modal (for Advisors/Leaders) */}
            {showPendingEditModal && selectedApp && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '500px' }}>
                        <h3>Edit Application - {selectedApp.application_id}</h3>
                        <p style={{ fontSize: '12px', color: '#666', marginBottom: '20px' }}>
                            You can only edit applications from the current month
                        </p>

                        <div className="form-group">
                            <label>Customer Name *</label>
                            <input
                                type="text"
                                value={editCustomerName}
                                onChange={(e) => setEditCustomerName(e.target.value)}
                                placeholder="Enter customer name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Customer Phone *</label>
                            <input
                                type="text"
                                value={editCustomerPhone}
                                onChange={(e) => setEditCustomerPhone(e.target.value)}
                                placeholder="Enter customer phone"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Customer Email</label>
                            <input
                                type="email"
                                value={editCustomerEmail}
                                onChange={(e) => setEditCustomerEmail(e.target.value)}
                                placeholder="Enter customer email (optional)"
                            />
                        </div>

                        {['car', 'bike'].includes(selectedApp.insurance_type) && (
                            <div className="form-group">
                                <label>Vehicle Number</label>
                                <input
                                    type="text"
                                    value={editVehicleNumber}
                                    onChange={(e) => setEditVehicleNumber(e.target.value)}
                                    placeholder="e.g., TN01AB1234"
                                />
                            </div>
                        )}

                        {!['car', 'bike'].includes(selectedApp.insurance_type) && (
                            <div className="form-group">
                                <label>Comments/Notes</label>
                                <textarea
                                    value={editComments}
                                    onChange={(e) => setEditComments(e.target.value)}
                                    placeholder="Enter any additional comments or notes"
                                    rows="3"
                                />
                            </div>
                        )}

                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowPendingEditModal(false);
                                    setSelectedApp(null);
                                    setEditCustomerName('');
                                    setEditCustomerPhone('');
                                    setEditCustomerEmail('');
                                    setEditVehicleNumber('');
                                    setEditComments('');
                                }}
                                disabled={editing}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-approve"
                                onClick={handlePendingEditSubmit}
                                disabled={editing}
                            >
                                {editing ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject/Cancel Modal */}
            {showRejectModal && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{userRole === 'super_admin' ? 'Reject Application' : 'Cancel Application'}</h3>
                        <div className="form-group">
                            <label>{userRole === 'super_admin' ? 'Rejection Comment *' : 'Cancellation Reason *'}</label>
                            <textarea
                                value={rejectionComment}
                                onChange={(e) => setRejectionComment(e.target.value)}
                                placeholder={userRole === 'super_admin' ? 'Enter reason for rejection' : 'Enter reason for cancellation'}
                                rows="4"
                            />
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                disabled={rejecting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                className="btn-danger"
                                disabled={rejecting}
                            >
                                {rejecting
                                    ? (userRole === 'super_admin' ? 'Rejecting...' : 'Cancelling...')
                                    : (userRole === 'super_admin' ? 'Reject' : 'Cancel Application')
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Application Form Modal */}
            {showNewApplicationForm && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
                        <GeneralInsuranceForm
                            onSuccess={() => {
                                setShowNewApplicationForm(false);
                                fetchApplications();
                                toast.success('Application submitted successfully!');
                            }}
                            onCancel={() => setShowNewApplicationForm(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GeneralInsuranceList;
